import React from 'react';
import styles from './LooseItemLocations.module.scss';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { IconButton, Paper, Table, TableBody, TableContainer } from '@material-ui/core';
import TableHeadComponent from 'components/SummaryComponents/TableHeadComponent/TableHeadComponent';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import CancelIcon from '@material-ui/icons/Cancel';
import FormInputText from 'components/FormComponents/FormInputText/FormInputText_v2';
import FormTextArea from 'components/FormComponents/FormTextArea/FormTextArea_v2';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import { useLooseItemSelector } from 'hooks/LooseItems';

import { FormSearchInput, FormServerAssetSearch } from 'components/FormComponents';

import { tableHeadConfig } from './tableHeadConfig';
import clsx from 'clsx';
import { getSum, pluck } from 'helpers/AppHelpers';

export default function LooseItemLocations() {
  const isMobile = useMobileViewport();
  const { control, clearErrors, getValues, setValue, formState } = useFormContext();
  const { fields, remove, append } = useFieldArray({ control, name: 'locations' });
  const { errors } = formState;

  const { creationForm } = useLooseItemSelector();
  const { locations } = creationForm;

  const isStatusAvailable = (row) => row?.status?.displayName === 'Available';
  const isRemovable = (row) => fields?.length !== 1 && isStatusAvailable(row);

  const handleRemoveClick = (index) => {
    clearErrors(`locations[${index}].byAheadAccount`);
    remove(index);
  };

  const handleAddRowClick = () => {
    const newRow = {
      location: locations[0],
      quantity: 0,
      byAheadAccount: 0,
      minimumQuantity: 0,
      status: { name: 'Available', displayName: 'Available' }
    };
    append(newRow);
  };

  const clearContainedInAsset = (index) => setValue(`locations[${index}].containedInAsset`, null);
  const handleSublocationSelect = (name, value, index) => {
    setValue(name, value);
    clearContainedInAsset(index);
  };
  const handleLocationSelect = (name, value, index) => {
    setValue(`locations[${index}].sublocation`, null);
    setValue(name, value);
    clearContainedInAsset(index);
  };

  const getSublocations = (locationId) =>
    locations?.find(({ id }) => id === locationId)?.sublocations || [];

  const getTotal = () => getSum(pluck(getValues('locations'), 'quantity'));
  const calculateTotal = () => setValue('quantity', getTotal());

  const forbiddenStatuses = ['InPicklist', 'InLeavelist', 'Rented', 'UsedInAssembly'];
  const isQuantityDisabled = (row) => forbiddenStatuses.includes(row?.status?.name);

  const enrichLocationOptions = (name) => {
    if (
      locations?.length &&
      getValues(name)?.id &&
      !locations.map(({ id }) => id).includes(getValues(name)?.id)
    ) {
      return locations.concat(getValues(name)).sort((a, b) => a.id > b.id);
    } else return locations;
  };

  const selectAsset = (name, value, index) => {
    setValue(name, value);

    if (!value?.id) return;

    const { currentLocationId, currentSublocationId } = value;

    const location = locations?.find(({ id }) => id === currentLocationId) || {};
    const sublocation =
      location.sublocations?.find(({ id }) => id === currentSublocationId) || null;

    if (location?.id) {
      setValue(`locations[${index}].location`, location);
      setValue(`locations[${index}].sublocation`, sublocation);
    }
  };

  return (
    <section className={styles.wrapper}>
      <TableContainer className={styles.table} component={Paper}>
        <Table>
          <TableHeadComponent config={tableHeadConfig} />
          <TableBody>
            {!!fields?.length &&
              fields.map((row, index) => (
                <StyledTableRow key={row.id}>
                  <StyledTableCell>
                    {isStatusAvailable(row) ? (
                      <FormSearchInput
                        name={`locations[${index}].location`}
                        options={enrichLocationOptions(`locations[${index}].location`)}
                        onSelect={handleLocationSelect}
                        classes={styles.inputs__location}
                        index={index}
                      />
                    ) : (
                      <span className={styles.disabled_cell}>{row?.location?.siteCode || ''}</span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {isStatusAvailable(row) ? (
                      <Controller
                        name={`locations[${index}].location`}
                        control={control}
                        render={() => (
                          <FormSearchInput
                            clearable
                            name={`locations[${index}].sublocation`}
                            options={
                              getSublocations(getValues(`locations[${index}].location`)?.id) || []
                            }
                            onSelect={handleSublocationSelect}
                            classes={styles.inputs__sublocation}
                            index={index}
                          />
                        )}
                      />
                    ) : (
                      <span className={styles.disabled_cell}>{row?.sublocation?.name || ''}</span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {isStatusAvailable(row) ? (
                      <FormServerAssetSearch
                        name={`locations[${index}].containedInAsset`}
                        max={100}
                        onSelect={(n, v) => selectAsset(n, v, index)}
                        getLabel={(option) => option?.drCode || ''}
                        getQuery={(searchQuery) => ({
                          pagination: { limit: 39, page: 1 },
                          filters: { isDeleted: false, prefixTypes: [2, 3, 4, 5], searchQuery }
                        })}
                        className={styles.inputs__containedInAsset}
                      />
                    ) : (
                      <span className={clsx(styles.disabled_cell, styles.noPadding)}>
                        {row?.containedInAsset?.drCode || ''}
                      </span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormTextArea
                      name={`locations[${index}].notes`}
                      options={{
                        max: 500,
                        minRows: 1,
                        disableCounter: true,
                        focus: true,
                        disableError: true
                      }}
                      classes={styles.inputs__description}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {row?.status?.displayName || ''}
                    {row?.status?.displayName === 'In Picklist' && ` (${row?.picklist?.name})`}
                    {row?.status?.displayName === 'In Leavelist' && ` (${row?.leavelist?.name})`}
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormInputText
                      name={`locations[${index}].minimumQuantity`}
                      options={{
                        focus: true,
                        type: 'quantity',
                        disableError: true,
                        isDisabled: isQuantityDisabled(row),
                        onlyNumbers: true
                      }}
                      classes={styles.inputs__field}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    className={clsx(
                      errors?.locations?.[index]?.byAheadAccount?.message && styles.error
                    )}>
                    <FormInputText
                      name={`locations[${index}].byAheadAccount`}
                      options={{
                        focus: true,
                        type: 'quantity',
                        disableError: true,
                        isDisabled: isQuantityDisabled(row),
                        onlyNumbers: true
                      }}
                      classes={styles.inputs__field}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormInputText
                      name={`locations[${index}].quantity`}
                      options={{
                        focus: true,
                        type: 'quantity',
                        disableError: true,
                        extraAction: calculateTotal,
                        isDisabled: isQuantityDisabled(row),
                        onlyNumbers: true
                      }}
                      classes={styles.inputs__field}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {isRemovable(row) && (
                      <IconButton onClick={() => handleRemoveClick(index)}>
                        <CancelIcon />
                      </IconButton>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.footer}>
        <div className={styles.left}>
          <span>[1]Buy Ahead Account</span>
          <Button className={styles.left__button} onClick={handleAddRowClick}>
            <AddIcon />
            <span>Add</span>
          </Button>
        </div>
        {!isMobile && (
          <Controller
            name="quantity"
            render={({ field: { value } }) => <p className={styles.total}>Total: {value || 0}</p>}
          />
        )}
      </div>
    </section>
  );
}
